import { apiClient } from "./api";

// User types
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  username?: string;
  bio?: string;
  location?: string;
  website?: string;
  expertise?: string;
  avatarUrl?: string;
  role: "ADMIN" | "CREATOR" | "USER";
  createdAt: string;
  updatedAt: string;
  stats: {
    campaignsCreated: number;
    feedbackGiven: number;
    commentsPosted: number;
    totalEngagement: number;
  };
}

export interface UpdateProfileRequest {
  name?: string;
  username?: string;
  bio?: string;
  location?: string;
  website?: string;
  expertise?: string;
}

export interface UserSettings {
  emailNotifications: boolean;
  projectUpdates: boolean;
  newFeedback: boolean;
  validationResults: boolean;
  marketingEmails: boolean;
}

export interface UpdateSettingsRequest {
  emailNotifications?: boolean;
  projectUpdates?: boolean;
  newFeedback?: boolean;
  validationResults?: boolean;
  marketingEmails?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface Notification {
  id: string;
  type: "comment" | "feedback" | "milestone" | "system" | "update" | "like";
  message: string;
  campaignId?: string;
  read: boolean;
  timestamp: string;
  user?: {
    name: string;
    avatar: string;
  };
}

interface BackendNotification {
  id: string;
  type: string;
  message: string;
  campaignId?: string;
  read: boolean;
  createdAt: string;
  relatedUser?: {
    name: string;
    avatarUrl?: string;
  };
}

export interface UserCampaign {
  id: string;
  title: string;
  description: string;
  bannerImage: string;
  positiveVotes: number;
  negativeVotes: number;
  feedbackCount: number;
  daysLeft?: number;
  status: "active" | "completed" | "draft";
  createdAt: string;
}

export interface UserActivity {
  id: string;
  type:
    | "campaign_created"
    | "comment_posted"
    | "feedback_given"
    | "campaign_updated";
  description: string;
  timestamp: string;
  campaignId?: string;
  campaignTitle?: string;
}

// Backend contribution response structure
interface BackendContribution {
  id: string;
  title: string;
  description: string;
  bannerImage: string;
  positiveVotes: number;
  negativeVotes: number;
  feedbackCount: number;
  status: string;
  createdAt: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  lastContribution?: {
    id: string;
    content?: string;
    answer?: string;
    createdAt: string;
    question?: {
      text: string;
      type: string;
    };
  };
  hasCommented: boolean;
  hasProvidedFeedback: boolean;
  hasBothContributions: boolean;
}

export interface UserContribution {
  id: string;
  campaignId: string;
  title: string;
  description: string;
  bannerImage: string;
  contributionType: "comment" | "feedback" | "bloom_score" | "response";
  contributionText: string;
  timestamp: string;
  positiveVotes: number;
  negativeVotes: number;
  feedbackCount: number;
  status: "active" | "completed" | "draft";
  hasCommented: boolean;
  hasProvidedFeedback: boolean;
  hasBothContributions: boolean;
}

// User API service
export const userApi = {
  // Get user profile
  async getProfile(): Promise<UserProfile> {
    return apiClient.get<UserProfile>("/auth/profile");
  },

  // Update user profile
  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    return apiClient.patch<UserProfile>("/auth/profile", data);
  },

  // Get user settings - Real API call
  async getSettings(): Promise<UserSettings> {
    return apiClient.get<UserSettings>("/auth/settings");
  },

  // Update user settings - Real API call
  async updateSettings(data: UpdateSettingsRequest): Promise<UserSettings> {
    return apiClient.patch<UserSettings>("/auth/settings", data);
  },

  // Change user password
  async changePassword(
    data: ChangePasswordRequest
  ): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/auth/change-password", data);
  },

  // Get user notifications
  async getNotifications(): Promise<Notification[]> {
    const response = await apiClient.get<BackendNotification[]>(
      "/notifications"
    );

    // Transform backend response to frontend format
    return response.map((notification) => ({
      id: notification.id,
      type: notification.type.toLowerCase() as Notification["type"],
      message: notification.message,
      campaignId: notification.campaignId,
      read: notification.read,
      timestamp: notification.createdAt,
      user: notification.relatedUser
        ? {
            name: notification.relatedUser.name,
            avatar: notification.relatedUser.avatarUrl || "",
          }
        : undefined,
    }));
  },

  // Mark notification as read
  async markNotificationAsRead(id: string): Promise<void> {
    await apiClient.post(`/notifications/${id}/read`);
  },

  // Mark all notifications as read
  async markAllNotificationsAsRead(): Promise<void> {
    await apiClient.post("/notifications/mark-all-read");
  },

  // Get user campaigns
  async getUserCampaigns(): Promise<UserCampaign[]> {
    return apiClient.get<UserCampaign[]>("/campaigns/my");
  },

  // Get user activity - Mock data for now
  async getUserActivity(): Promise<UserActivity[]> {
    // Mock data since backend endpoint doesn't exist yet
    return Promise.resolve([
      {
        id: "1",
        type: "campaign_created",
        description: "Created campaign 'TaskFlow AI'",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        campaignId: "campaign-1",
        campaignTitle: "TaskFlow AI",
      },
      {
        id: "2",
        type: "comment_posted",
        description: "Posted a comment on 'SmartHome Hub'",
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        campaignId: "campaign-2",
        campaignTitle: "SmartHome Hub",
      },
      {
        id: "3",
        type: "feedback_given",
        description: "Provided feedback on 'EcoTracker'",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        campaignId: "campaign-3",
        campaignTitle: "EcoTracker",
      },
      {
        id: "4",
        type: "campaign_updated",
        description: "Updated campaign 'TaskFlow AI'",
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        campaignId: "campaign-1",
        campaignTitle: "TaskFlow AI",
      },
    ]);
  },

  // Get user contributions - Now calls real backend endpoint
  async getUserContributions(): Promise<UserContribution[]> {
    const backendContributions = await apiClient.get<BackendContribution[]>(
      "/auth/contributions"
    );

    console.log("backendContributions", backendContributions);

    // Transform backend response to frontend format
    return backendContributions.map((contribution) => {
      // Determine contribution type and text based on available data
      let contributionType:
        | "comment"
        | "feedback"
        | "bloom_score"
        | "response" = "response";
      let contributionText = "Provided feedback to campaign questions";

      if (contribution.lastContribution) {
        if (
          "content" in contribution.lastContribution &&
          contribution.lastContribution.content
        ) {
          // This is a comment
          contributionType = "comment";
          contributionText = contribution.lastContribution.content;
        } else if (
          "answer" in contribution.lastContribution &&
          contribution.lastContribution.answer
        ) {
          // This is a response
          contributionType = "response";
          contributionText = `Answered: ${
            contribution.lastContribution.question?.text || "Campaign question"
          }`;
        }
      }

      return {
        id: contribution.id,
        campaignId: contribution.id,
        title: contribution.title,
        description: contribution.description,
        bannerImage: contribution.bannerImage,
        contributionType,
        contributionText,
        timestamp:
          contribution.lastContribution?.createdAt || contribution.createdAt,
        positiveVotes: contribution.positiveVotes,
        negativeVotes: contribution.negativeVotes,
        feedbackCount: contribution.feedbackCount,
        status: contribution.status as "active" | "completed" | "draft",
        hasCommented: contribution.hasCommented,
        hasProvidedFeedback: contribution.hasProvidedFeedback,
        hasBothContributions: contribution.hasBothContributions,
      };
    });
  },
};
